import { useEffect, useState } from "react";
import { FileText, ArrowUpRight, Download, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Page } from "@/components/Page";
import { api, downloadBlob, formatError } from "@/lib/api";

const DOC_TYPES = [
  ["GI 3A", "Authorised User application", "GI-3A", "01"],
  ["Statement of Case", "The story and evidence of your GI product", "Statement-of-Case", "02"],
  ["NOC", "No Objection Certificate on the proprietor's letterhead", "NOC", "03"],
];

export default function Documents() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);

  const load = () => api.get("/my-submissions").then(r => setSubmissions(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const downloadPdf = async (sub) => {
    try { await downloadBlob(`/submissions/${sub.id}/pdf`, `${sub.form_type}-${sub.id.slice(0, 8)}.pdf`); toast.success("PDF downloaded"); }
    catch (err) { toast.error(formatError(err)); }
  };

  const remove = async (sub) => {
    if (!window.confirm(`Delete this ${sub.form_type} submission? This cannot be undone.`)) return;
    try {
      await api.delete(`/submissions/${sub.id}`);
      setSubmissions(x => x.filter(y => y.id !== sub.id));
      toast.success("Submission deleted");
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <Page title="Apply for GI Authorised User" subtitle="Fill the supplied blank formats, print them, sign the originals, and send them with support documents.">
      <div className="notice">
        <FileText size={20}/>
        <div>
          <b>Use the supplied source formats</b>
          <p>These forms reproduce the wording from your uploaded GI 3A, Statement of Case, and NOC documents. They are blank working copies for your review and signature.</p>
        </div>
      </div>
      <div className="doc-grid">
        {DOC_TYPES.map(([title, text, type, no]) => (
          <div className="doc-card" key={type}>
            <div className="doc-number">{no}</div>
            <h3>{title}</h3>
            <p>{text}</p>
            <button
              className="secondary-btn"
              onClick={() => navigate(`/documents/${type}`)}
              data-testid={`open-${type.toLowerCase()}-button`}
            >
              Open blank form <ArrowUpRight size={16}/>
            </button>
          </div>
        ))}
      </div>

      {/* Seller's saved submissions with PDF downloads */}
      <section className="my-submissions" data-testid="my-submissions">
        <header>
          <h3>My saved applications</h3>
          <p>Every form you have saved so far. Download the PDF, print, sign, and send the hard copy to True Vision.</p>
        </header>
        {submissions.length === 0 ? (
          <p className="muted" data-testid="my-submissions-empty">No saved forms yet. Fill a form above and click "Save filled form" to see it here.</p>
        ) : (
          <div className="admin-table" data-testid="my-submissions-list">
            <div className="admin-row admin-head"><span>Form</span><span>Status</span><span>Saved on</span><span></span></div>
            {submissions.map(s => (
              <div className="admin-row" key={s.id} data-testid={`my-sub-${s.id}`}>
                <span><b>{s.form_type}</b></span>
                <span className="pill amber">{s.status}</span>
                <span>{new Date(s.created_at).toLocaleString()}</span>
                <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button className="secondary-btn" onClick={() => downloadPdf(s)} data-testid={`my-sub-download-${s.id}`}>
                    <Download size={12} /> PDF
                  </button>
                  <button className="danger-btn" onClick={() => remove(s)} data-testid={`my-sub-delete-${s.id}`}>
                    <Trash2 size={12} /> Delete
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </Page>
  );
}
